import {
  AccrualMethod, CarryoverMode, ResetMode, TenureUnit,
  type LeaveAccrualRule, type LeavePlanSetupRequest, type LeavePlanSetupResponse, type LeavePlanSetupSettings,
} from '../../time-and-leave/types';

/** The Figma "Leave Plan Setup" dialog as plain strings, so every field can be typed into, validated and compared for unsaved changes. */
export interface RuleForm {
  toValue: string;
  toUnit: string;
  rate: string;
  perHours: string;
  maxBalance: string;
}

export interface LeavePlanForm {
  /** '' (nothing chosen), '1' Vacation, '2' Sick, 'custom' (a new custom type) or `plan:<leavePlanId>` (a custom type the company already has). */
  typeKey: string;
  title: string;
  effectiveDate: string;
  startingBalance: string;
  accrualMethod: string;
  hoursPerPayPeriod: string;
  hoursEarned: string;
  perHoursWorked: string;
  hoursPerYear: string;
  rules: RuleForm[];
  resetMode: string;
  resetMonth: string;
  resetDay: string;
  /** 'none' (no maximum) or 'set'. */
  maxMode: 'none' | 'set';
  maxBalance: string;
  carryoverMode: string;
  carryoverLimit: string;
  negativeMode: 'no' | 'yes';
  maxNegative: string;
}

export type FormErrors = Record<string, string>;

export const MAX_HOURS = 8760;
export const MAX_RULES = 20;

export const emptyRule = (): RuleForm => ({ toValue: '', toUnit: String(TenureUnit.Months), rate: '', perHours: '', maxBalance: '' });

export const emptyForm = (_today: string): LeavePlanForm => ({
  typeKey: '', title: '', effectiveDate: '', startingBalance: '', accrualMethod: '',
  hoursPerPayPeriod: '', hoursEarned: '', perHoursWorked: '', hoursPerYear: '', rules: [],
  resetMode: '', resetMonth: '1', resetDay: '1', maxMode: 'none', maxBalance: '',
  carryoverMode: '', carryoverLimit: '', negativeMode: 'no', maxNegative: '',
});

const str = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n));

/** The saved setup as the dialog shows it. Each rule's "From" is the previous rule's "To" (the first starts at 0), so only "To" is typed. */
export function formFromSetup(s: LeavePlanSetupResponse): LeavePlanForm {
  return {
    typeKey: s.leavePlanType === 5 ? `plan:${s.leavePlanId}` : String(s.leavePlanType),
    title: s.title ?? '',
    effectiveDate: s.effectiveDate,
    startingBalance: String(s.startingBalanceHours),
    accrualMethod: String(s.accrualMethod),
    hoursPerPayPeriod: str(s.hoursPerPayPeriod), hoursEarned: str(s.hoursEarned), perHoursWorked: str(s.perHoursWorked), hoursPerYear: str(s.hoursPerYear),
    rules: s.rules.map((r) => ({
      toValue: str(r.toTenureValue), toUnit: String(r.toTenureUnit ?? TenureUnit.Months),
      rate: String(r.rate), perHours: str(r.perHoursWorked), maxBalance: String(r.maximumBalanceHours),
    })),
    resetMode: String(s.resetMode), resetMonth: str(s.resetMonth) || '1', resetDay: str(s.resetDay) || '1',
    maxMode: s.maximumBalanceHours === null ? 'none' : 'set', maxBalance: str(s.maximumBalanceHours),
    carryoverMode: String(s.carryoverMode), carryoverLimit: str(s.carryoverLimitHours),
    negativeMode: s.negativeBalanceAllowed ? 'yes' : 'no', maxNegative: str(s.maximumNegativeHours),
  };
}

/** Hours: a plain number, at most two decimal places, never negative and never more than a year of hours. */
export function parseHours(raw: string, { allowZero }: { allowZero: boolean }): number | null {
  const t = raw.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const n = Number(t);
  if (n > MAX_HOURS || (!allowZero && n === 0)) return null;
  return n;
}

const parseWhole = (raw: string): number | null => (/^\d+$/.test(raw.trim()) && Number(raw) <= 600 ? Number(raw) : null);

const monthsOf = (value: number, unit: number) => (unit === TenureUnit.Years ? value * 12 : value);

/** Days in each month of a non-leap year: February 29 is not accepted as a reset date. */
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function methodUsesRules(method: string): boolean {
  return method === String(AccrualMethod.EveryPayPeriod) || method === String(AccrualMethod.HoursWorked) || method === String(AccrualMethod.Yearly);
}

export function validateForm(f: LeavePlanForm, mode: { isEdit: boolean; today: string }): FormErrors {
  const e: FormErrors = {};
  if (!mode.isEdit) {
    if (!f.typeKey) e.typeKey = 'Select a leave plan type.';
    if (f.typeKey === 'custom' && !f.title.trim()) e.title = 'Enter a title for the custom leave plan.';
    if (f.effectiveDate && f.effectiveDate < '1990-01-01') e.effectiveDate = 'The effective date cannot be before 1990.';
    const startingBalance = f.startingBalance.trim() === '' ? 0 : parseHours(f.startingBalance, { allowZero: true });
    if (startingBalance === null) e.startingBalance = 'Enter hours from 0 to 8,760 with at most two decimals.';
    else if (f.accrualMethod === String(AccrualMethod.Manual) && f.startingBalance.trim() === '') e.startingBalance = 'Enter the starting balance in hours.';
  }

  if (!f.accrualMethod) e.accrualMethod = 'Select an accrual method.';
  if (f.accrualMethod === String(AccrualMethod.EveryPayPeriod) && parseHours(f.hoursPerPayPeriod, { allowZero: false }) === null) {
    e.hoursPerPayPeriod = 'Enter the hours earned each pay period, more than 0.';
  }
  if (f.accrualMethod === String(AccrualMethod.HoursWorked)) {
    if (parseHours(f.hoursEarned, { allowZero: false }) === null) e.hoursEarned = 'Enter the hours earned, more than 0.';
    if (parseHours(f.perHoursWorked, { allowZero: false }) === null) e.perHoursWorked = 'Enter the hours worked, more than 0.';
  }
  if (f.accrualMethod === String(AccrualMethod.Yearly) && parseHours(f.hoursPerYear, { allowZero: false }) === null) {
    e.hoursPerYear = 'Enter the hours earned each year, more than 0.';
  }

  if (methodUsesRules(f.accrualMethod)) {
    if (f.rules.length > MAX_RULES) e.rules = `A plan can have at most ${MAX_RULES} rules.`;
    let previousEnd = 0;
    f.rules.forEach((r, i) => {
      const last = i === f.rules.length - 1;
      const prefix = `rule${i}`;
      if (r.toValue.trim() === '') {
        if (!last) e[`${prefix}.to`] = 'Enter where this rule ends. Only the last rule can be open-ended.';
      } else {
        const to = parseWhole(r.toValue);
        if (to === null) e[`${prefix}.to`] = 'Enter a whole number.';
        else if (monthsOf(to, Number(r.toUnit)) <= previousEnd) e[`${prefix}.to`] = 'This must end after the previous rule ends.';
        else previousEnd = monthsOf(to, Number(r.toUnit));
      }
      if (parseHours(r.rate, { allowZero: false }) === null) e[`${prefix}.rate`] = 'Enter hours, more than 0.';
      if (f.accrualMethod === String(AccrualMethod.HoursWorked) && parseHours(r.perHours, { allowZero: false }) === null) e[`${prefix}.perHours`] = 'Enter hours worked, more than 0.';
      if (parseHours(r.maxBalance, { allowZero: false }) === null) e[`${prefix}.max`] = 'Enter hours, more than 0.';
    });
  }

  if (!f.resetMode) e.resetMode = 'Choose when the balance resets.';
  if (f.resetMode === String(ResetMode.CustomDate)) {
    const month = Number(f.resetMonth);
    const day = parseWhole(f.resetDay);
    if (!(month >= 1 && month <= 12)) e.resetMonth = 'Select a month.';
    else if (day === null || day < 1 || day > DAYS_IN_MONTH[month - 1]!) e.resetDay = `Enter a day from 1 to ${DAYS_IN_MONTH[month - 1]}.`;
  }

  if (f.maxMode === 'set' && parseHours(f.maxBalance, { allowZero: false }) === null) e.maxBalance = 'Enter the maximum allowed hours, more than 0.';

  if (f.maxMode === 'set' && !e.maxBalance && !e.startingBalance && (parseHours(f.startingBalance, { allowZero: true }) ?? 0) > (parseHours(f.maxBalance, { allowZero: false }) ?? 0)) {
    e.maxBalance = 'The maximum balance cannot be less than the starting balance.';
  }

  if (!f.carryoverMode) e.carryoverMode = 'Choose what happens to unused time.';
  if (f.carryoverMode === String(CarryoverMode.CarryUpTo) && parseHours(f.carryoverLimit, { allowZero: false }) === null) {
    e.carryoverLimit = 'Enter the maximum carryover hours, more than 0.';
  }

  if (f.negativeMode === 'yes' && f.maxNegative.trim() !== '' && parseHours(f.maxNegative, { allowZero: false }) === null) {
    e.maxNegative = 'Enter the maximum negative balance, more than 0, or leave it blank for no limit.';
  }
  return e;
}

/** The settings the server stores. Fields that do not apply to the chosen choices are sent as null so the server never keeps a stale value. */
export function settingsFromForm(f: LeavePlanForm): LeavePlanSetupSettings {
  const method = Number(f.accrualMethod);
  const hours = (raw: string) => parseHours(raw, { allowZero: true }) as number;
  const rules: LeaveAccrualRule[] = [];
  if (methodUsesRules(f.accrualMethod)) {
    let from = 0;
    let fromUnit: number = TenureUnit.Months;
    for (const r of f.rules) {
      const open = r.toValue.trim() === '';
      const to = open ? null : Number(r.toValue);
      rules.push({
        fromTenureValue: from, fromTenureUnit: fromUnit, toTenureValue: to, toTenureUnit: open ? null : Number(r.toUnit),
        rate: hours(r.rate), perHoursWorked: method === AccrualMethod.HoursWorked ? hours(r.perHours) : null, maximumBalanceHours: hours(r.maxBalance),
      });
      if (to !== null) {
        from = to;
        fromUnit = Number(r.toUnit);
      }
    }
  }
  return {
    accrualMethod: method,
    hoursPerPayPeriod: method === AccrualMethod.EveryPayPeriod ? hours(f.hoursPerPayPeriod) : null,
    hoursEarned: method === AccrualMethod.HoursWorked ? hours(f.hoursEarned) : null,
    perHoursWorked: method === AccrualMethod.HoursWorked ? hours(f.perHoursWorked) : null,
    hoursPerYear: method === AccrualMethod.Yearly ? hours(f.hoursPerYear) : null,
    rules,
    resetMode: Number(f.resetMode),
    resetMonth: f.resetMode === String(ResetMode.CustomDate) ? Number(f.resetMonth) : null,
    resetDay: f.resetMode === String(ResetMode.CustomDate) ? Number(f.resetDay) : null,
    maximumBalanceHours: f.maxMode === 'set' ? hours(f.maxBalance) : null,
    carryoverMode: Number(f.carryoverMode),
    carryoverLimitHours: f.carryoverMode === String(CarryoverMode.CarryUpTo) ? hours(f.carryoverLimit) : null,
    negativeBalanceAllowed: f.negativeMode === 'yes',
    maximumNegativeHours: f.negativeMode === 'yes' && f.maxNegative.trim() !== '' ? hours(f.maxNegative) : null,
  };
}

export function requestFromForm(f: LeavePlanForm): LeavePlanSetupRequest {
  const custom = f.typeKey === 'custom' || f.typeKey.startsWith('plan:');
  return {
    leavePlanType: custom ? 5 : Number(f.typeKey),
    title: custom ? f.title.trim() : null,
    // Left blank, the plan takes effect today (the Figma shows the field empty).
    effectiveDate: f.effectiveDate || new Date().toISOString().slice(0, 10),
    startingBalanceHours: f.startingBalance.trim() === '' ? 0 : hours0(f.startingBalance),
    ...settingsFromForm(f),
  };
}

const hours0 = (raw: string) => parseHours(raw, { allowZero: true }) as number;

/** The rule table's "From" cell: the previous rule's end, and 0 for the first rule. */
export function ruleFromLabel(rules: RuleForm[], index: number): string {
  if (index === 0) return '0';
  const prev = rules[index - 1]!;
  return prev.toValue.trim() === '' ? '—' : prev.toValue;
}

export function ruleFromUnit(rules: RuleForm[], index: number): string {
  return index === 0 ? String(TenureUnit.Months) : rules[index - 1]!.toUnit;
}
